//TODO:  should there be a separate section by article type or all articles in once section?

import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { AlertDialogService } from '@Core/services/proprietary/alert-dialog/alert-dialog.service';
import { EditStatusType, StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { SetDirty } from '../page/page.actions';
import { ProductLineConfigObjectState } from '../productline-config/productline-config-object.state';
import { AddressState } from './address.state';
import {
    AddArticle,
    AddArticleInterestPointer,
    AddProductsToArticle,
    AddSubArticle,
    AddVehicleDriverSubArticle,
    CloneArticle,
    CloneSubArticle,
    DeleteArticle,
    DeleteSubArticle,
    DeleteSubArticleOnParentDeleted,
    RemoveArticleInterestPointer,
    RemoveProductsFromArticle,
    RemoveSubLocationFromArticle,
    ReplaceLocationOnArticles,
    RestoreArticle,
    RestoreSubArticle,
    SetArticleChanged,
    SetCurrentArticle,
    SetPrimaryPerilProduct,
    UpdateArticle,
    UpdateSubArticle,
    UpdateVehicleDriverSubArticle
} from './article.actions';
import { AddNewProfileForClonedArticle, LinkProfileToArticle } from './contract-object.actions';
import { ContractObjectDefaults } from './contract-object.defaults';
import { ArticleNodeType, ArticleToSubArticleMap, ProfileArticles, SubArticleNodeType } from './contract-object.types';
import { ContractState } from './contract.state';
import { EntityState } from './entity.state';
import { AddContractDetailItem, RemoveContractDetailItem, UpdateLine } from './line.actions';
import { LineState } from './line.state';
import { LocationState } from './location.state';
import { PhoneState } from './phone.state';
import {
    AddProductEntities,
    DeleteProduct,
    RestoreProduct,
    SetProductAutoDisplay,
    UpdateProduct,
    UpdateProductTotals
} from './product.actions';
import { ProductState } from './product.state';
import { AddProfile } from './profile.actions';

export interface ArticleStateModel {
    currentID: any;
    article: any;
    subArticle: any;
}

@State<ArticleStateModel>({
    name: 'articleState',
    defaults: {
        currentID: null,
        article: {},
        subArticle: {}
    }
})
@Injectable()
export class ArticleState {
    constructor(private store: Store, private helper: StateHelperService, private alertDialog: AlertDialogService) { }

    //******* SELECTORS *********

    @Selector()
    static currentArticle(state: ArticleStateModel) {
        if (state.article[state.currentID]) {
            return state.article[state.currentID];
        } else {
            return {};
        }
    }

    @Selector()
    static currentArticleID(state: ArticleStateModel) {
        return state.currentID;
    }

    @Selector()
    static articleByID(state: ArticleStateModel) {
        return (articleID: any) => {
            let result: any = {};
            for (var i in state.article) {
                if (state.article[i].Article_ID == articleID) {
                    result = state.article[i];
                }
            }
            return result;
        };
    }

    //TODO: do we want these shortcuts by article type that don't require parameters?'
    @Selector()
    static CDAAVehiclesByLine(state: ArticleStateModel) {
        return (lineID: string) => {
            let result: any = [];
            for (var i in state.article) {
                if (state.article[i].Line_ID == lineID && state.article[i].nodeType == 'CDAA_Vehicle') {
                    result.push(state.article[i]);
                }
            }
            return result;
        };
    }
    @Selector()
    static CDAAVehicleCountByLine(state: ArticleStateModel) {
        //return article count of a particular type
        return (lineID: string) => {
            let count = 0;
            for (var i in state.article) {
                if (state.article[i].Line_ID == lineID && state.article[i].nodeType == 'CDAA_Vehicle') {
                    count++;
                }
            }
            return count;
        };
    }

    //Dynamic Article and Article count selectors based on ArticleNodeType
    @Selector()
    static articlesByLineAndType(state: ArticleStateModel) {
        return (lineID: string, articleNodeType: ArticleNodeType) => {
            let result: any = [];
            for (var i in state.article) {
                if (state.article[i].Line_ID == lineID && state.article[i].nodeType == articleNodeType) {
                    result.push(state.article[i]);
                }
            }
            return result;
        };
    }

    @Selector()
    static articlesByType(state: ArticleStateModel) {
        return (articleNodeType: ArticleNodeType[]) => {
            let result: any = [];
            for (var i in state.article) {
                if (articleNodeType.includes(state.article[i].nodeType)) {
                    result.push(state.article[i]);
                }
            }
            return result;
        };
    }

    @Selector()
    static articleCountByLineAndType(state: ArticleStateModel) {
        return (lineID: string, articleNodeType: ArticleNodeType) => {
            let count = 0;
            for (var i in state.article) {
                if (state.article[i].Line_ID == lineID && state.article[i].nodeType == articleNodeType) {
                    count++;
                }
            }
            return count;
        };
    }

    //Subarticle selector based on array of subarticle id's
    @Selector()
    static subArticlesByIDs(state: ArticleStateModel) {
        //return subarticles based on an array of subarticle ID's passed in
        return (subArticleIDs: any[]) => {
            let result: any = [];
            if (subArticleIDs) {
                subArticleIDs.forEach(function (thisID) {
                    result.push(state.subArticle[thisID]);
                });
            }
            return result;
        };
    }

    static driverVehiclesByVehNum(vehNum: string) {
        return createSelector([ArticleState], (state: ArticleStateModel) => {
            let result: any = [];
            if (vehNum) {
                for (var i in state.subArticle) {
                    if (
                        state.subArticle[i].nodeType == SubArticleNodeType.CDAA_Driver_Vehicle &&
                        state.subArticle[i].Book_Veh_Num == vehNum
                    ) {
                        result.push(state.subArticle[i]);
                    }
                }
            }
            return result || [];
        });
    }

    @Selector()
    static vehicleCodeSet(state: ArticleStateModel) {
        let vehicleList: any = [];
        let vehicle: any = {};
        let vehicleDesc: any;

        for (var i in state.article) {
            vehicle = {};
            vehicleDesc = '';
            if (state.article[i].nodeType == ArticleNodeType.CDAA_Vehicle && state.article[i].Edit_Status != 'D') {
                if (state.article[i].Veh_Year && state.article[i].Veh_Year != '') {
                    vehicleDesc = state.article[i].Veh_Year;
                }
                if (state.article[i].Veh_Make && state.article[i].Veh_Make != '') {
                    vehicleDesc += ' ' + state.article[i].Veh_Make;
                }
                if (state.article[i].Veh_Model && state.article[i].Veh_Model != '') {
                    vehicleDesc += ' ' + state.article[i].Veh_Model;
                }
                if (state.article[i].Book_Veh_Num && state.article[i].Book_Veh_Num != '') {
                    vehicleDesc += ' (' + state.article[i].Book_Veh_Num + ')';
                }
                vehicle = { Code: state.article[i].Book_Veh_Num, Code_Desc: vehicleDesc };
                vehicleList.push(vehicle);
            }
        }
        return vehicleList || [];
    }

    @Selector()
    static blanketCodeSet(state: ArticleStateModel) {
        let blanketList: any = [];
        let blanket: any = {};
        let blanketDesc: any;

        blanket = { Code: '', Code_Desc: '(none)' };
        blanketList.push(blanket);

        for (var i in state.article) {
            blanket = {};
            blanketDesc = '';
            if (state.article[i].nodeType == ArticleNodeType.CDPC_Blanket_Rating && state.article[i].Edit_Status != 'D') {
                if (state.article[i].Blanket_Num && state.article[i].Blanket_Num != '') {
                    blanketDesc = state.article[i].Blanket_Num;
                }
                if (state.article[i].Blanket_Desc && state.article[i].Blanket_Desc != '') {
                    blanketDesc += ' : ' + state.article[i].Blanket_Desc;
                }

                blanket = { Code: state.article[i].Blanket_Num, Code_Desc: blanketDesc };
                blanketList.push(blanket);
            }
        }
        return blanketList;
    }

    //******* ACTIONS *********

    @Action(SetCurrentArticle)
    setCurrentArticle(sc: StateContext<ArticleStateModel>, action: SetCurrentArticle) {
        let articleID = null;
        if (action.article) {
            if (action.article.Article_ID) {
                articleID = action.article.Article_ID;
            }
        }
        sc.patchState({ currentID: articleID });
    }

    @Action(AddProductsToArticle)
    addProductsToArticle(sc: StateContext<ArticleStateModel>, action: AddProductsToArticle) {
        const state = sc.getState();
        const articleEntities = state.article;
        const productEntities = this.store.selectSnapshot<any>(ProductState.productEntities);
        const reporting = this.store.selectSnapshot(ContractState.isReporting);

        let updatedArticle = cloneDeep(articleEntities[action.articleID]);
        let lineID = updatedArticle.Line_ID;

        let currentCostIDs = updatedArticle.Product || [];
        let newCostIDs: any = [];
        let newProducts = {};
        let addedCount = 0;

        let updateProduct: any = {};

        //loop through array of supplied products
        for (var j in action.products) {
            let newProductCode = action.products[j].Product_Code;
            let skipAdd = false;

            for (var k in currentCostIDs) {
                if (productEntities[currentCostIDs[k]]) {
                    if (productEntities[currentCostIDs[k]].Product_Code == newProductCode) {
                        skipAdd = true;
                        updateProduct = cloneDeep(productEntities[currentCostIDs[k]]);

                        break;
                    }
                }
            }
            if (!skipAdd) {
                //add product if it doesn't already exist
                let newCostID = this.helper.getTempID();
                let newProductID = this.helper.getTempID();
                newProducts[newCostID] = cloneDeep(action.products[j]);

                //Only products added from a ProductSet should include Optional_Ind property.
                if (
                    action.autoDisplay &&
                    newProducts[newCostID].hasOwnProperty('Optional_Ind') &&
                    newProducts[newCostID].Optional_Ind == 'Y'
                ) {
                    this.helper.setItemAvailable(newProducts[newCostID]); //Set optional products as Available (Edit_Status = '?') if autoDisplayed.
                } else {
                    this.helper.setItemAdded(newProducts[newCostID]);

                    //Remove Default, Mandatory, Optional flags if not added via AutoDisplay products.
                    if (!action.autoDisplay) {
                        delete newProducts[newCostID].Optional_Ind;
                        delete newProducts[newCostID].Default_Ind;
                        delete newProducts[newCostID].Mandatory_Ind;
                    }

                    addedCount++;
                }

                newProducts[newCostID].Cost_ID = newCostID;
                if (!newProducts[newCostID].Product_ID) {
                    newProducts[newCostID].Product_ID = newProductID; //Only use newProductID if Product_ID wasn't included.
                }

                newProducts[newCostID].Article_ID = action.articleID;
                //temp defaults -- ultimately get these from product conig
                if (!newProducts[newCostID].Rejected_Ind) {
                    newProducts[newCostID].Rejected_Ind = 'N';
                }

                if (!newProducts[newCostID].Statutory_Limit_Ind) {
                    newProducts[newCostID].Statutory_Limit_Ind = 'N';
                }

                if (reporting) {
                    newProducts[newCostID].Fully_Earned_Ind = 'Y';
                } else {
                    if (!newProducts[newCostID].Fully_Earned_Ind) {
                        newProducts[newCostID].Fully_Earned_Ind = 'N';
                    }
                }

                if (!reporting) {
                    newProducts[newCostID].Reporting_Fully_Earned_Ind = 'N';
                } else {
                    if (!newProducts[newCostID].Reporting_Fully_Earned_Ind) {
                        newProducts[newCostID].Reporting_Fully_Earned_Ind = 'N';
                    }
                }

                if (!newProducts[newCostID].Net_Change_Method) {
                    newProducts[newCostID].Net_Change_Method = 'P';
                }
                if (!newProducts[newCostID].Full_Term_Amount) {
                    newProducts[newCostID].Full_Term_Amount = '0';
                }
                if (!newProducts[newCostID].Prev_Full_Term_Amount) {
                    newProducts[newCostID].Prev_Full_Term_Amount = '0';
                }
                if (!newProducts[newCostID].Prev_Written_Amount) {
                    newProducts[newCostID].Prev_Written_Amount = '0';
                }
                newProducts[newCostID].Net_Change_Amount = newProducts[newCostID].Full_Term_Amount;
                newCostIDs.push(newCostID);
            } else {
                if (action.autoDisplay) {
                    sc.dispatch(new SetProductAutoDisplay(updateProduct, cloneDeep(action.products[j])));
                } else {
                    if (updateProduct && updateProduct.Edit_Status && updateProduct.Edit_Status == EditStatusType.Delete) {
                        sc.dispatch(new RestoreProduct(updateProduct.Cost_ID));
                    }
                }
            }
        }

        if (newCostIDs.length > 0) {
            if (addedCount > 0) {
                this.helper.setItemChanged(updatedArticle, updatedArticle.Edit_Status);
            }
            updatedArticle.Product = [...currentCostIDs, ...newCostIDs];

            sc.patchState({ article: { ...state.article, [action.articleID]: updatedArticle } });

            sc.dispatch(new AddProductEntities(newProducts, false));

            if (addedCount > 0) {
                sc.dispatch([new UpdateLine({ Line_ID: lineID }), new UpdateProductTotals(), new SetDirty()]);
            }
        }
    }

    @Action(RemoveProductsFromArticle)
    removeProductsFromArticle(sc: StateContext<ArticleStateModel>, action: RemoveProductsFromArticle) {
        const state = sc.getState();
        const articleEntities = state.article;

        let updatedArticle = cloneDeep(articleEntities[action.articleID]);
        let lineID = updatedArticle.Line_ID;
        this.helper.setItemChanged(updatedArticle, updatedArticle.Edit_Status);

        let updatedProductIDs: any[] = updatedArticle.Product || [];

        //loop through array of supplied products and remove supplied products
        for (var j in action.costIDs) {
            updatedProductIDs = this.helper.removeArrayItemByValue(updatedProductIDs, action.costIDs[j]);
        }

        updatedArticle.Product = updatedProductIDs;

        sc.patchState({ article: { ...state.article, [action.articleID]: updatedArticle } });

        sc.dispatch([new UpdateLine({ Line_ID: lineID }), new UpdateProductTotals()]);
    }

    @Action(CloneArticle)
    cloneArticle(sc: StateContext<ArticleStateModel>, action: CloneArticle) {
        let origID = action.articleID;
        let newID = this.helper.getTempID();

        const state = sc.getState();
        const articleEntities = state.article;
        const productEntities = this.store.selectSnapshot<any>(ProductState.productEntities);

        let newArticle = cloneDeep(articleEntities[origID]);
        this.helper.setItemAdded(newArticle);
        newArticle.Article_ID = newID;
        newArticle.Article_GUID = null;

        delete newArticle.Interest;

        // set values based on article type  (like Book_Veh_Num)   TODO:  Should this logic be in the ObjectService?
        switch (newArticle.nodeType) {
            case ArticleNodeType.CDMA_Misc_Article:
                break;
            case ArticleNodeType.CDMA_ERP:
                break;
            case ArticleNodeType.CDAA_Vehicle:
                newArticle.Book_Veh_Num = this.helper.getNextItemNumber(state.article, 'Book_Veh_Num', newArticle.nodeType);
                newArticle.Comp_Veh_Num = this.helper.getNextItemNumber(state.article, 'Comp_Veh_Num', newArticle.nodeType);
                newArticle.VIN = null;
                break;
            case ArticleNodeType.CDAA_Driver:
                newArticle.Book_Driver_Num = this.helper.getNextItemNumber(
                    state.article,
                    'Book_Driver_Num',
                    newArticle.nodeType
                );
                delete newArticle.CDAA_Driver_Vehicle; // clone does not include copy of SubArticles items yet
                delete newArticle.CDAA_Driver_Violation; // clone does not include copy of SubArticles items yet
                delete newArticle.CDAA_Driver_Discount; // clone does not include copy of SubArticle items yet
                delete newArticle.CDAA_Driver_FinResp;
                break;
            case ArticleNodeType.CDAC_Filing:
                delete newArticle.CDAC_Filing_Detail;
                break;
            case ArticleNodeType.CDAC_Underwriting:
                break;
            case ArticleNodeType.CDAC_Auto_Symbol:
                break;
            case ArticleNodeType.CDAC_State_Rating:
                break;
            case ArticleNodeType.CDAC_Truck_Equipment:
                break;
            case ArticleNodeType.CDAC_Truck_Interchange:
                break;
            case ArticleNodeType.CDAC_Truck_Zone:
                delete newArticle.CDAC_Truck_Terminal;
                break;
            case ArticleNodeType.CDAP_All_Risk:
                break;
            case ArticleNodeType.CDAP_Underwriting:
                break;
            case ArticleNodeType.CDGL_Class_Rating:
                delete newArticle.CDGL_Class_Product; // clone does not include copy of SubArticles yet
                break;
            case ArticleNodeType.CDGL_State_Rating:
                break;
            case ArticleNodeType.CDGL_Claims_Made:
                break;
            case ArticleNodeType.CDGL_Underwriting:
                break;
            case ArticleNodeType.CDPL_Rating:
                break;
            case ArticleNodeType.CDPL_Individuals:
                break;
            case ArticleNodeType.CDPL_Underwriting:
                break;
            case ArticleNodeType.CDMM_Facility:
                delete newArticle.CDMM_Individual_Schedule; // clone does not include copy of SubArticles yet
                break;
            case ArticleNodeType.CDMM_Underwriting:
                break;
            case ArticleNodeType.CDMM_Individual:
                break;
            case ArticleNodeType.CDAM_Animal:
                break;
            case ArticleNodeType.CDDO_Rating:
                break;
            case ArticleNodeType.CDEP_Rating:
                break;
            case ArticleNodeType.CDCR_Rating:
                delete newArticle.CDCR_Employees; // clone does not include copy of SubArticles yet
                delete newArticle.CDCR_Money_Securities; // clone does not include copy of SubArticles yet
                delete newArticle.CDCR_Safe_Vault_Info; // clone does not include copy of SubArticles yet
                break;
            case ArticleNodeType.CDEO_Rating:
                break;
            case ArticleNodeType.CDHM_Homeowners:
                this.helper.setItemAdded(newArticle.CDHM_Underwriting);
                this.helper.setItemAdded(newArticle.CDHM_Inspection);
                this.helper.setItemAdded(newArticle.CDHM_State);
                this.helper.setItemAdded(newArticle.CDHM_Replace_Cost);
                this.helper.setItemAdded(newArticle.CDHM_Mobile);
                break;
            case ArticleNodeType.CDHM_Mobile_Home:
                break;
            case ArticleNodeType.CDHM_CPL:
                break;
            case ArticleNodeType.CDIM_Accounts_Receivable:
                break;
            case ArticleNodeType.CDIM_Builders_Risk:
                break;
            case ArticleNodeType.CDIM_Builders_Risk_Underwriting:
                break;
            case ArticleNodeType.CDIM_Communications:
                delete newArticle.CDIM_Communications_TowerAntenna; // clone does not include copy of SubArticles yet
                break;
            case ArticleNodeType.CDIM_EDP:
                break;
            case ArticleNodeType.CDIM_EDP_Underwriting:
                break;
            case ArticleNodeType.CDIM_Equipment_Floater:
                break;
            case ArticleNodeType.CDIM_Schedule_Summary:
                delete newArticle.CDIM_Schedule_Items; // clone does not include copy of SubArticles yet
                break;
            case ArticleNodeType.CDIM_Subcontractor_Underwriting:
                break;
            case ArticleNodeType.CDIM_Transportation:
                delete newArticle.CDIM_Transportation_Detail; // clone does not include copy of SubArticle items yet
                break;
            case ArticleNodeType.CDIM_Transportation_Rigging:
                break;
            case ArticleNodeType.CDEO_Job:
                break;
            case ArticleNodeType.CDEO_Underwriting:
                break;
            case ArticleNodeType.CDGK_Rating:
                break;
            case ArticleNodeType.CDGD_PhysDam_Loc:
                break;
            case ArticleNodeType.CDGK_Underwriting:
                break;
            case ArticleNodeType.CDJB_Individuals:
                break;
            case ArticleNodeType.CDJB_Rating:
                delete newArticle.CDJB_Rating_Exhibition; // clone does not include copy of SubArticles yet
                delete newArticle.CDJB_Rating_Off_Premises; // clone does not include copy of SubArticles yet
                break;
            case ArticleNodeType.CDJB_Underwriting:
                delete newArticle.CDJB_Underwriting_Protect; // clone does not include copy of SubArticles yet
                break;
            case ArticleNodeType.CDGD_Rating:
                break;
            case ArticleNodeType.CDGD_Underwriting:
                break;
            case ArticleNodeType.CDUX_Care_Custody_Control:
                break;
            case ArticleNodeType.CDUX_Location:
                break;
            case ArticleNodeType.CDUX_Underlying:
                delete newArticle.CDUX_Primary; // clone does not include copy of SubArticles yet
                delete newArticle.CDUX_Vehicle;
                delete newArticle.CDUX_Watercraft;
                break;
            case ArticleNodeType.CDPC_Blanket_Rating:
                newArticle.Blanket_Num = this.helper.getNextItemNumber(state.article, 'Blanket_Num', newArticle.nodeType);
                break;
            case ArticleNodeType.CDPC_Class_Rating:
                break;
            case ArticleNodeType.CDPC_Glass_Schedule:
                break;
            case ArticleNodeType.CDPC_Glass_Underwriting:
                break;
            case ArticleNodeType.CDPC_Underwriting:
                delete newArticle.CDPC_Underwriting_Protect; // clone does not include copy of SubArticles yet
                break;
            case ArticleNodeType.CDPC_Underwriting_Location:
                break;
            case ArticleNodeType.CDPO_Rating:
                break;
            case ArticleNodeType.CDPX_Rating:
                break;
            case ArticleNodeType.CDSB_Rating:
                break;
            case ArticleNodeType.CDLL_Rating:
                break;
            case ArticleNodeType.CDLL_Underwriting:
                break;
            case ArticleNodeType.CDPP_Schedule:
                delete newArticle.CDPP_Item; // clone does not include copy of SubArticles yet
                break;
            case ArticleNodeType.CDWC_Class_Rating:
                break;
            case ArticleNodeType.CDWC_Individual:
                break;
            case ArticleNodeType.CDWC_Other_State:
                break;
            case ArticleNodeType.CDWC_State_Rating:
                break;
            case ArticleNodeType.CDWC_Underwriting:
                break;
            case ArticleNodeType.Line_Wide:
                break;
            default:
                console.error('Unsupported Article NodeType provided for cloneArticle()  Parameter=' + newArticle.NodeType);
                return;
        }

        // Clone the products
        let newProductIDs: any = [];
        let newProducts = {};

        if (action.copyProducts == true && newArticle.Product) {
            for (var i in newArticle.Product) {
                let costID = newArticle.Product[i];
                let newCostID = this.helper.getTempID();
                let newProductID = this.helper.getTempID();
                newProducts[newCostID] = cloneDeep(productEntities[costID]);
                this.helper.setItemAdded(newProducts[newCostID]);
                newProducts[newCostID].Cost_ID = newCostID;
                newProducts[newCostID].Product_ID = newProductID;
                newProducts[newCostID].Article_ID = newID;
                newProducts[newCostID].Prev_Full_Term_Amount = 0.0;
                newProducts[newCostID].Prev_Written_Amount = 0.0;
                newProductIDs.push(newCostID);
            }
        }
        newArticle.Product = newProductIDs;

        sc.patchState({ article: { ...state.article, [newID]: newArticle }, currentID: newID }); //add to article and set the new article as current.

        let lineID = newArticle.Line_ID;
        sc.dispatch([new AddContractDetailItem(lineID, newID, newArticle.nodeType), new UpdateLine({ Line_ID: lineID })]);

        if (newProductIDs.length > 0) {
            sc.dispatch([new AddProductEntities(newProducts, true)]);
        }

        //TODO: IS THIS A VALID APPROACH?   Probably ok as long as copies is not a crazy high number.
        if (action.copies > 1) {
            sc.dispatch([new CloneArticle(action.articleID, action.copies - 1, action.copyProducts)]);
        }

        // Clone the Contract Profile (If article is linked to a profile) and then link the new article and profile.
        if (ProfileArticles.indexOf(newArticle.nodeType) != -1) {
            sc.dispatch([new AddNewProfileForClonedArticle(newArticle)]);
        }
    }

    @Action(SetArticleChanged)
    SetArticleChanged(sc: StateContext<ArticleStateModel>, action: SetArticleChanged) {
        const state = sc.getState();
        let articleID = action.articleID;
        let updateArticle: any = cloneDeep(state.article[articleID]);
        let lineID = state.article[articleID].Line_ID;

        this.helper.setItemChanged(updateArticle, state.article[articleID].Edit_Status);
        sc.patchState({
            article: {
                ...state.article,
                [articleID]: {
                    ...state.article[articleID],
                    ...updateArticle
                }
            }
        });

        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(UpdateArticle)
    updateArticle(sc: StateContext<ArticleStateModel>, action: UpdateArticle) {
        const state = sc.getState();
        let articleID = action.article.Article_ID;
        const currentArticle = state.article[articleID];
        let updateArticle: any = { ...action.article };
        let lineID = state.article[articleID].Line_ID;
        let lineCode = this.store.selectSnapshot(LineState.lineByLineID)(lineID).Line_Code;
        let productEntities = this.store.selectSnapshot<any>(ProductState.productsByIDs)(currentArticle.Product);

        if (state.article[articleID].Loc_Num !== action.article.Loc_Num) {
            // clear Sub Loc Num if Loc Num changes
            if (state.article[articleID].Sub_Loc_Num) {
                updateArticle.Sub_Loc_Num = '';
            }
        }

        // Place article specific update logic here
        switch (currentArticle.nodeType) {
            case ArticleNodeType.CDPC_Glass_Underwriting:
                if (
                    updateArticle.Unusual_Setting_Ind &&
                    currentArticle.Unusual_Setting_Ind &&
                    updateArticle.Unusual_Setting_Ind != 'Y' &&
                    currentArticle.Unusual_Setting_Ind == 'Y'
                ) {
                    updateArticle.Unusual_Setting_Value = '';
                }
                if (
                    updateArticle.Insure_Tape_Ind &&
                    currentArticle.Insure_Tape_Ind &&
                    updateArticle.Insure_Tape_Ind != 'Y' &&
                    currentArticle.Insure_Tape_Ind == 'Y'
                ) {
                    updateArticle.Tape_Value = '';
                }
                if (
                    updateArticle.Insure_Lettering_Ind &&
                    currentArticle.Insure_Lettering_Ind &&
                    updateArticle.Insure_Lettering_Ind != 'Y' &&
                    currentArticle.Insure_Lettering_Ind == 'Y'
                ) {
                    updateArticle.Lettering_Value = '';
                }
                break;
            case ArticleNodeType.CDHM_Homeowners:
                // Need a way to detect a change to these sub nodes without looping through the object and comparing values
                // everytime a change to the article happens
                if (updateArticle.CDHM_Underwriting) {
                    this.helper.setItemChanged(updateArticle.CDHM_Underwriting, currentArticle.CDHM_Underwriting.Edit_Status);
                }
                if (updateArticle.CDHM_Inspection) {
                    this.helper.setItemChanged(updateArticle.CDHM_Inspection, currentArticle.CDHM_Inspection.Edit_Status);
                }
                if (updateArticle.CDHM_State) {
                    this.helper.setItemChanged(updateArticle.CDHM_State, currentArticle.CDHM_State.Edit_Status);
                }

                if (updateArticle.CDHM_Replace_Cost) {
                    this.helper.setItemChanged(updateArticle.CDHM_Replace_Cost, currentArticle.CDHM_Replace_Cost.Edit_Status);
                }
                if (updateArticle.CDHM_Mobile) {
                    this.helper.setItemChanged(updateArticle.CDHM_Mobile, currentArticle.CDHM_Mobile.Edit_Status);
                }

                break;
        }

        this.helper.setItemChanged(updateArticle, state.article[articleID].Edit_Status);

        sc.patchState({
            article: {
                ...state.article,
                [articleID]: {
                    ...currentArticle,
                    ...updateArticle
                }
            }
        });

        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);

        //Article Specific Post Update Logic
        switch (currentArticle.nodeType) {
            case ArticleNodeType.CDPC_Class_Rating:
            case ArticleNodeType.CDPC_Blanket_Rating:
                if (
                    updateArticle.Primary_Peril &&
                    updateArticle.Primary_Peril != '' &&
                    updateArticle.Primary_Peril != currentArticle.Primary_Peril
                ) {
                    let productConfig = this.store.selectSnapshot(ProductLineConfigObjectState.productConfig)(
                        lineCode,
                        updateArticle.Primary_Peril
                    );
                    let product: any = {};

                    if (productConfig && productConfig.Product_Code) {
                        product.Product_Code = productConfig.Product_Code;
                        product.Product_Code_Desc = productConfig.Product_Desc;
                        product.Product_Cost_Type = productConfig.Product_Cost_Type;
                        product.Display_Order = productConfig.Display_Order;
                        product.Fully_Earned_Ind = productConfig.Fully_Earned_Ind;
                        product.Net_Change_Method = productConfig.Net_Change_Method;
                        product.Reporting_Fully_Earned_Ind = productConfig.Reporting_Fully_Earned_Ind;

                        if (currentArticle.Insurance_Amount && currentArticle.Insurance_Amount != '') {
                            if (productConfig.Limit_1_Use == 'O' || productConfig.Limit_1_Use == 'M') {
                                product.Limit_1 = currentArticle.Insurance_Amount;
                            }
                        }

                        sc.dispatch(new AddProductsToArticle(parseInt(articleID, 10), [product]));
                    }

                    if (currentArticle.Primary_Peril && currentArticle.Primary_Peril != '') {
                        if (productEntities.length > 0) {
                            for (var i in productEntities) {
                                if (productEntities[i].Product_Code == currentArticle.Primary_Peril) {
                                    sc.dispatch(new DeleteProduct(productEntities[i].Cost_ID));
                                    break;
                                }
                            }
                        }
                    }
                }

                if (
                    updateArticle.Insurance_Amount &&
                    updateArticle.Insurance_Amount != '' &&
                    updateArticle.Insurance_Amount != currentArticle.Insurance_Amount
                ) {
                    if (currentArticle.Primary_Peril && currentArticle.Primary_Peril != '') {
                        if (productEntities.length > 0) {
                            for (var i in productEntities) {
                                if (productEntities[i].Product_Code == currentArticle.Primary_Peril) {
                                    let productConfig = this.store.selectSnapshot(ProductLineConfigObjectState.productConfig)(
                                        lineCode,
                                        currentArticle.Primary_Peril
                                    );

                                    if (productConfig.Limit_1_Use == 'O' || productConfig.Limit_1_Use == 'M') {
                                        let product = cloneDeep(productEntities[i]);

                                        product.Limit_1 = updateArticle.Insurance_Amount;
                                        sc.dispatch(new UpdateProduct(product));
                                    }

                                    break;
                                }
                            }
                        }
                    }
                }

                break;
        }
    }

    @Action(AddArticle)
    addArticle(sc: StateContext<ArticleStateModel>, action: AddArticle) {
        const state = sc.getState();
        let lineID = action.lineID;
        let newID = this.helper.getTempID();
        let newArticle = this.helper.createDefaultItem(ContractObjectDefaults, action.articleType, action.article);
        this.helper.setItemAdded(newArticle);
        newArticle.Article_ID = newID;
        newArticle.nodeType = action.articleType;
        newArticle.Line_ID = lineID;

        // set article specific values (like Book_Veh_Num)
        switch (action.articleType) {
            case ArticleNodeType.CDMA_Misc_Article:
                break;
            case ArticleNodeType.CDMA_ERP:
                break;
            case ArticleNodeType.CDAA_Vehicle:
                newArticle.Book_Veh_Num = this.helper.getNextItemNumber(
                    state.article,
                    'Book_Veh_Num',
                    ArticleNodeType.CDAA_Vehicle
                );
                break;
            case ArticleNodeType.CDAA_Driver:
                newArticle.Book_Driver_Num = this.helper.getNextItemNumber(
                    state.article,
                    'Book_Driver_Num',
                    newArticle.nodeType
                );
                break;
            case ArticleNodeType.CDAC_Filing:
                let consumer = this.store.selectSnapshot(EntityState.consumer);
                newArticle.Filing_Name = consumer.Entity_Name;

                if (consumer.Addresss && consumer.Addresses.Address) {
                    consumer.Addresses.Address.forEach((id) => {
                        let address = this.store.selectSnapshot(AddressState.addressByID)(id);

                        if (address.Dflt_Address_Ind === 'Y') {
                            newArticle.Address_Line_1 = address.Address_Line_1;
                            newArticle.Zip = address.Zip;
                            newArticle.City = address.City;
                            newArticle.State = address.State;
                            newArticle.County = address.County;
                            newArticle.Country = address.Country;
                        }
                    });
                }
                if (consumer.Phones && consumer.Phones.Phone) {
                    consumer.Phones.Phone.forEach((id) => {
                        let phone = this.store.selectSnapshot(PhoneState.phoneByID)(id);

                        if (phone.Dflt_Phone_Ind === 'Y') {
                            newArticle.Phone_Num = phone.Phone_Num;
                        }
                    });
                }
                break;
            case ArticleNodeType.CDAC_Underwriting:
                break;
            case ArticleNodeType.CDAC_Auto_Symbol:
                break;
            case ArticleNodeType.CDAC_State_Rating:
                break;
            case ArticleNodeType.CDAC_Truck_Equipment:
                break;
            case ArticleNodeType.CDAC_Truck_Interchange:
                break;
            case ArticleNodeType.CDAC_Truck_Zone:
                break;
            case ArticleNodeType.CDAP_All_Risk:
                break;
            case ArticleNodeType.CDAP_Underwriting:
                break;
            case ArticleNodeType.CDGL_Class_Rating:
                break;
            case ArticleNodeType.CDGL_State_Rating:
                break;
            case ArticleNodeType.CDGL_Claims_Made:
                break;
            case ArticleNodeType.CDGL_Underwriting:
                break;
            case ArticleNodeType.CDPL_Rating:
                break;
            case ArticleNodeType.CDPL_Individuals:
                break;
            case ArticleNodeType.CDPL_Underwriting:
                break;
            case ArticleNodeType.CDMM_Facility:
                break;
            case ArticleNodeType.CDMM_Underwriting:
                break;
            case ArticleNodeType.CDMM_Individual:
                break;
            case ArticleNodeType.CDAM_Animal:
                break;
            case ArticleNodeType.CDDO_Rating:
                break;
            case ArticleNodeType.CDEP_Rating:
                break;
            case ArticleNodeType.CDCR_Rating:
                break;
            case ArticleNodeType.CDEO_Rating:
                break;
            case ArticleNodeType.CDHM_Homeowners:
                this.helper.setItemAdded(newArticle.CDHM_Underwriting);
                this.helper.setItemAdded(newArticle.CDHM_Inspection);
                this.helper.setItemAdded(newArticle.CDHM_State);
                this.helper.setItemAdded(newArticle.CDHM_Replace_Cost);
                this.helper.setItemAdded(newArticle.CDHM_Mobile);
                break;
            case ArticleNodeType.CDHM_Mobile_Home:
                break;
            case ArticleNodeType.CDHM_CPL:
                break;
            case ArticleNodeType.CDIM_Accounts_Receivable:
                break;
            case ArticleNodeType.CDIM_Builders_Risk:
                break;
            case ArticleNodeType.CDIM_Builders_Risk_Underwriting:
                break;
            case ArticleNodeType.CDIM_Communications:
                break;
            case ArticleNodeType.CDIM_EDP:
                break;
            case ArticleNodeType.CDIM_EDP_Underwriting:
                break;
            case ArticleNodeType.CDIM_Equipment_Floater:
                break;
            case ArticleNodeType.CDIM_Schedule_Summary:
                break;
            case ArticleNodeType.CDIM_Subcontractor_Underwriting:
                break;
            case ArticleNodeType.CDIM_Transportation:
                break;
            case ArticleNodeType.CDIM_Transportation_Rigging:
                break;
            case ArticleNodeType.CDEO_Job:
                break;
            case ArticleNodeType.CDEO_Underwriting:
                break;
            case ArticleNodeType.CDGK_Rating:
                break;
            case ArticleNodeType.CDGD_PhysDam_Loc:
                break;
            case ArticleNodeType.CDGK_Underwriting:
                break;
            case ArticleNodeType.CDJB_Individuals:
                break;
            case ArticleNodeType.CDJB_Rating:
                break;
            case ArticleNodeType.CDJB_Underwriting:
                break;
            case ArticleNodeType.CDGD_Rating:
                break;
            case ArticleNodeType.CDGD_Underwriting:
                break;
            case ArticleNodeType.CDUX_Care_Custody_Control:
                break;
            case ArticleNodeType.CDUX_Location:
                break;
            case ArticleNodeType.CDUX_Underlying:
                break;
            case ArticleNodeType.CDPC_Blanket_Rating:
                newArticle.Blanket_Num = this.helper.getNextItemNumber(state.article, 'Blanket_Num', newArticle.nodeType);
                break;
            case ArticleNodeType.CDPC_Class_Rating:
                break;
            case ArticleNodeType.CDPC_Glass_Schedule:
                break;
            case ArticleNodeType.CDPC_Glass_Underwriting:
                break;
            case ArticleNodeType.CDPC_Underwriting:
                break;
            case ArticleNodeType.CDPC_Underwriting_Location:
                break;
            case ArticleNodeType.CDPO_Rating:
                break;
            case ArticleNodeType.CDPX_Rating:
                break;
            case ArticleNodeType.CDSB_Rating:
                break;
            case ArticleNodeType.CDLL_Rating:
                break;
            case ArticleNodeType.CDLL_Underwriting:
                break;
            case ArticleNodeType.CDPP_Schedule:
                break;
            case ArticleNodeType.CDWC_Class_Rating:
                break;
            case ArticleNodeType.CDWC_Individual:
                break;
            case ArticleNodeType.CDWC_Other_State:
                break;
            case ArticleNodeType.CDWC_State_Rating:
                break;
            case ArticleNodeType.CDWC_Underwriting:
                break;
            case ArticleNodeType.Line_Wide:
                break;

            default:
                console.error('Unsupported Article NodeType provided for addArticle(). Parameter=' + newArticle.nodeType);
                return;
        }

        if (!newArticle.Loc_Num || newArticle.Loc_Num == '') {
            newArticle.Loc_Num = '';
            newArticle.Sub_Loc_Num = '';

            let locations = this.store.selectSnapshot(LocationState.locations);
            let locNum = '';
            let locCount = 0;

            //Loop through and get the count of non-deleted locations.
            for (var i in locations) {
                if (locations[i].Edit_Status != 'D') {
                    locCount++;
                    locNum = locations[i].Loc_Num;
                }
            }

            if (locCount == 1 && locNum && locNum != '') {
                newArticle.Loc_Num = locNum;
            }
        }

        sc.patchState({
            currentID: newID,
            article: { ...state.article, [newID]: newArticle }
        });

        sc.dispatch([new AddContractDetailItem(lineID, newID, newArticle.nodeType), new UpdateLine({ Line_ID: lineID })]);

        if (ProfileArticles.indexOf(newArticle.nodeType) != -1 && action.profile) {
            let profile = action.profile;

            if (profile.Profile_ID && profile.Profile_ID != '') {
                sc.dispatch(new LinkProfileToArticle(profile.Profile_ID, newArticle.Article_ID));
            } else {
                sc.dispatch(new AddProfile(action.profile, newArticle.Article_ID));
            }
        }
    }

    @Action(DeleteArticle)
    deleteArticle(sc: StateContext<ArticleStateModel>, action: DeleteArticle) {
        const state = sc.getState();
        let article = { ...state.article }; //shallow copy article from state
        const articleID = action.articleID;
        let nodeType = article[articleID].nodeType;
        let lineID = article[articleID].Line_ID;

        if (nodeType == ArticleNodeType.CDAA_Vehicle) {
            let vehNum = article[articleID].Book_Veh_Num;
            for (var subArticleID in state.subArticle) {
                if (
                    state.subArticle[subArticleID].nodeType == SubArticleNodeType.CDAA_Driver_Vehicle &&
                    state.subArticle[subArticleID].Book_Veh_Num == vehNum
                ) {
                    sc.dispatch([new DeleteSubArticle(state.subArticle[subArticleID].Article_ID, subArticleID)]);
                }
            }
        }

        let subArticleTypes = ArticleToSubArticleMap[nodeType];
        if (subArticleTypes) {
            for (const subType of subArticleTypes) {
                let subArticles = article[articleID][subType];
                if (subArticles) {
                    for (var i in subArticles) {
                        let subID = subArticles[i];
                        if (subID && subID.startsWith('-')) {
                            sc.dispatch([new DeleteSubArticle(articleID, subID)]);
                        } else {
                            sc.dispatch([new DeleteSubArticleOnParentDeleted(articleID, subID)]);
                        }
                    }
                }
            }
        }

        this.helper.setItemDeleted(article, articleID);
        sc.patchState({ article: article });
        if (articleID < 0) {
            sc.dispatch([new RemoveContractDetailItem(lineID, articleID.toString(), nodeType)]);
        } else {
            let products = article[articleID].Product;
            if (products) {
                let costIDs = [];
                for (var costID in products) {
                    costIDs.push(products[costID]);
                }
                sc.dispatch([new RemoveProductsFromArticle(articleID, costIDs)]);
            }
        }
        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(RestoreArticle)
    restoreArticle(sc: StateContext<ArticleStateModel>, action: RestoreArticle) {
        const state = sc.getState();
        let article = { ...state.article }; //shallow copy article from state
        let nodeType = article[action.articleID].nodeType;
        let lineID = article[action.articleID].Line_ID;

        let subArticleTypes = ArticleToSubArticleMap[nodeType];
        if (subArticleTypes) {
            for (const subType of subArticleTypes) {
                let subArticles = article[action.articleID][subType];
                if (subArticles) {
                    for (var i in subArticles) {
                        sc.dispatch([new RestoreSubArticle(action.articleID, subArticles[i])]);
                    }
                }
            }
        }

        this.helper.setItemRestored(article, action.articleID);
        sc.patchState({ article: article });
        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(ReplaceLocationOnArticles)
    replaceLocationOnArticles(sc: StateContext<ArticleStateModel>, action: ReplaceLocationOnArticles) {
        // Changes Loc_Num on all articles linked to the From loc_num
        const state = sc.getState();
        let changedArticles = {};

        for (var i in state.article) {
            if (state.article[i].Loc_Num && state.article[i].Loc_Num == action.fromLocNum) {
                if (state.article[i].Sub_Loc_Num && action.fromLocNum !== action.toLocNum) {
                    changedArticles[i] = { ...state.article[i], Loc_Num: action.toLocNum, Sub_Loc_Num: '' };
                } else {
                    changedArticles[i] = { ...state.article[i], Loc_Num: action.toLocNum };
                }
            }
        }

        sc.patchState({
            article: { ...state.article, ...changedArticles }
        });
    }

    @Action(RemoveSubLocationFromArticle)
    removeSubLocationFromArticle(sc: StateContext<ArticleStateModel>, action: RemoveSubLocationFromArticle) {
        // set Sub_Loc_Num to empty string on article when SubLoc is deleted
        const state = sc.getState();
        let changedArticles = {};

        for (var i in state.article) {
            if (state.article[i].Loc_Num && state.article[i].Loc_Num == action.locNum) {
                if (state.article[i].Sub_Loc_Num && state.article[i].Sub_Loc_Num == action.subLocNum) {
                    changedArticles[i] = { ...state.article[i], Sub_Loc_Num: '' };
                } else {
                    changedArticles[i] = { ...state.article[i] };
                }
            }
        }

        sc.patchState({
            article: { ...state.article, ...changedArticles }
        });
    }

    @Action(CloneSubArticle)
    cloneSubArticle(sc: StateContext<ArticleStateModel>, action: CloneSubArticle) {
        let origID = action.subArticleID;
        let newID = this.helper.getTempID();

        const state = sc.getState();
        const subArticleEntities = state.subArticle;

        let newSubArticle = cloneDeep(subArticleEntities[origID]);
        this.helper.setItemAdded(newSubArticle);
        let newPointerID = newID + '-' + newSubArticle.nodeType;

        let articleID = action.articleID;
        let updateArticle = cloneDeep(state.article[articleID]);
        if (updateArticle) {
            this.helper.setItemChanged(updateArticle, updateArticle.Edit_Status);
        }
        // set values based on sub_article type
        switch (newSubArticle.nodeType) {
            case SubArticleNodeType.CDGL_Class_Product:
                newSubArticle.GLP_SubArticle_ID = newID;
                newSubArticle.GLP_GUID = null;
                updateArticle.CDGL_Class_Product = this.helper.addArrayItem(updateArticle.CDGL_Class_Product, newPointerID);
                break;
            case SubArticleNodeType.CDAA_Driver_Vehicle:
                newSubArticle.DVH_SubArticle_ID = newID;
                newSubArticle.DVH_GUID = null;
                if (updateArticle) {
                    updateArticle.CDAA_Driver_Vehicle = this.helper.addArrayItem(updateArticle.CDAA_Driver_Vehicle, newPointerID);
                }
                break;
            case SubArticleNodeType.CDAA_Driver_Violation:
                newSubArticle.DVI_SubArticle_ID = newID;
                newSubArticle.DVI_GUID = null;
                updateArticle.CDAA_Driver_Violation = this.helper.addArrayItem(
                    updateArticle.CDAA_Driver_Violation,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDMM_Individual_Schedule:
                newSubArticle.Indiv_SubArticle_ID = newID;
                newSubArticle.MIS_GUID = null;
                updateArticle.CDMM_Individual_Schedule = this.helper.addArrayItem(
                    updateArticle.CDMM_Individual_Schedule,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDAA_Driver_Discount:
                newSubArticle.DDS_SubArticle_ID = newID;
                newSubArticle.DDS_GUID = null;
                updateArticle.CDAA_Driver_Discount = this.helper.addArrayItem(updateArticle.CDAA_Driver_Discount, newPointerID);
                break;
            case SubArticleNodeType.CDAA_Driver_FinResp:
                newSubArticle.DFR_SubArticle_ID = newID;
                newSubArticle.DFR_GUID = null;
                updateArticle.CDAA_Driver_FinResp = this.helper.addArrayItem(updateArticle.CDAA_Driver_FinResp, newPointerID);
                break;
            case SubArticleNodeType.CDAC_Filing_Detail:
                newSubArticle.FIL_SubArticle_ID = newID;
                newSubArticle.FIL_GUID = null;
                updateArticle.CDAC_Filing_Detail = this.helper.addArrayItem(updateArticle.CDAC_Filing_Detail, newPointerID);
                break;
            case SubArticleNodeType.CDPC_Underwriting_Protect:
                newSubArticle.PTI_SubArticle_ID = newID;
                newSubArticle.PTI_GUID = null;
                updateArticle.CDPC_Underwriting_Protect = this.helper.addArrayItem(
                    updateArticle.CDPC_Underwriting_Protect,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDAC_Truck_Terminal:
                newSubArticle.TTM_SubArticle_ID = newID;
                newSubArticle.TTM_GUID = null;
                updateArticle.CDAC_Truck_Terminal = this.helper.addArrayItem(updateArticle.CDAC_Truck_Terminal, newPointerID);
                break;
            case SubArticleNodeType.CDUX_Primary:
                newSubArticle.PRI_SubArticle_ID = newID;
                newSubArticle.PRI_GUID = null;
                updateArticle.CDUX_Primary = this.helper.addArrayItem(updateArticle.CDUX_Primary, newPointerID);
                break;
            case SubArticleNodeType.CDUX_Vehicle:
                newSubArticle.VEH_SubArticle_ID = newID;
                newSubArticle.VEH_GUID = null;
                updateArticle.CDUX_Vehicle = this.helper.addArrayItem(updateArticle.CDUX_Vehicle, newPointerID);
                break;
            case SubArticleNodeType.CDUX_Watercraft:
                newSubArticle.WAT_SubArticle_ID = newID;
                newSubArticle.WAT_GUID = null;
                updateArticle.CDUX_Watercraft = this.helper.addArrayItem(updateArticle.CDUX_Watercraft, newPointerID);
                break;
            case SubArticleNodeType.CDIM_Communications_TowerAntenna:
                newSubArticle.CTA_SubArticle_ID = newID;
                newSubArticle.CTA_GUID = null;
                updateArticle.CDIM_Communications_TowerAntenna = this.helper.addArrayItem(
                    updateArticle.CDIM_Communications_TowerAntenna,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDIM_Schedule_Items:
                newSubArticle.CIM_SubArticle_ID = newID;
                newSubArticle.CIM_GUID = null;
                updateArticle.CDIM_Schedule_Items = this.helper.addArrayItem(updateArticle.CDIM_Schedule_Items, newPointerID);
                break;
            case SubArticleNodeType.CDIM_Transportation_Detail:
                newSubArticle.TRD_SubArticle_ID = newID;
                newSubArticle.TRD_GUID = null;
                updateArticle.CDIM_Transportation_Detail = this.helper.addArrayItem(
                    updateArticle.CDIM_Transportation_Detail,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDIM_Builders_Risk_Rigging_Item:
                newSubArticle.BRI_SubArticle_ID = newID;
                newSubArticle.BRI_GUID = null;
                updateArticle.CDIM_Builders_Risk_Rigging_Item = this.helper.addArrayItem(
                    updateArticle.CDIM_Builders_Risk_Rigging_Item,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDCR_Employees:
                newSubArticle.CRE_SubArticle_ID = newID;
                newSubArticle.CRE_GUID = null;
                updateArticle.CDCR_Employees = this.helper.addArrayItem(updateArticle.CDCR_Employees, newPointerID);
                break;
            case SubArticleNodeType.CDCR_Money_Securities:
                newSubArticle.CRS_SubArticle_ID = newID;
                newSubArticle.CRS_GUID = null;
                updateArticle.CDCR_Money_Securities = this.helper.addArrayItem(
                    updateArticle.CDCR_Money_Securities,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDCR_Safe_Vault_Info:
                newSubArticle.CRV_SubArticle_ID = newID;
                newSubArticle.CRV_GUID = null;
                updateArticle.CDCR_Safe_Vault_Info = this.helper.addArrayItem(updateArticle.CDCR_Safe_Vault_Info, newPointerID);
                break;
            case SubArticleNodeType.CDJB_Rating_Exhibition:
                newSubArticle.JBE_SubArticle_ID = newID;
                newSubArticle.JBE_GUID = null;
                updateArticle.CDJB_Rating_Exhibition = this.helper.addArrayItem(
                    updateArticle.CDJB_Rating_Exhibition,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDJB_Rating_Off_Premises:
                newSubArticle.JBO_SubArticle_ID = newID;
                newSubArticle.JBO_GUID = null;
                updateArticle.CDJB_Rating_Off_Premises = this.helper.addArrayItem(
                    updateArticle.CDJB_Rating_Off_Premises,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDJB_Underwriting_Protect:
                newSubArticle.JBP_SubArticle_ID = newID;
                newSubArticle.JBP_GUID = null;
                updateArticle.CDJB_Underwriting_Protect = this.helper.addArrayItem(
                    updateArticle.CDJB_Underwriting_Protect,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDPP_Item:
                newSubArticle.PPI_SubArticle_ID = newID;
                newSubArticle.PPI_GUID = null;
                updateArticle.CDPP_Item = this.helper.addArrayItem(updateArticle.CDPP_Item, newPointerID);
                break;
            default:
                console.error(
                    'Unsupported SubArticle NodeType provided for cloneSubArticle()  Parameter=' + newSubArticle.NodeType
                );
                return;
        }

        sc.patchState({
            subArticle: { ...state.subArticle, [newPointerID]: newSubArticle }
        });

        if (updateArticle) {
            sc.dispatch(new UpdateArticle(updateArticle));
        }

        //TODO: IS THIS A VALID APPROACH?   Probably ok as long as copies is not a crazy high number.
        if (action.copies > 1) {
            sc.dispatch([new CloneSubArticle(action.articleID, action.subArticleID, action.copies - 1)]);
        }
    }

    @Action(UpdateSubArticle)
    updateSubArticle(sc: StateContext<ArticleStateModel>, action: UpdateSubArticle) {
        const state = sc.getState();

        let subArticleID = null;
        let updateSubArticle: any = { ...action.subArticle }; //need DEEP copy?

        let articleID = action.articleID;
        //let updateArticle = cloneDeep(state.article[articleID]);

        switch (updateSubArticle.nodeType) {
            case SubArticleNodeType.CDGL_Class_Product:
                subArticleID = updateSubArticle.GLP_SubArticle_ID;
                break;
            case SubArticleNodeType.CDAA_Driver_Vehicle:
                subArticleID = updateSubArticle.DVH_SubArticle_ID;
                break;
            case SubArticleNodeType.CDAA_Driver_Violation:
                subArticleID = updateSubArticle.DVI_SubArticle_ID;
                break;
            case SubArticleNodeType.CDMM_Individual_Schedule:
                subArticleID = updateSubArticle.Indiv_SubArticle_ID;
                break;
            case SubArticleNodeType.CDAA_Driver_Discount:
                subArticleID = updateSubArticle.DDS_SubArticle_ID;
                break;
            case SubArticleNodeType.CDAA_Driver_FinResp:
                subArticleID = updateSubArticle.DFR_SubArticle_ID;
                break;
            case SubArticleNodeType.CDAC_Filing_Detail:
                subArticleID = updateSubArticle.FIL_SubArticle_ID;
                break;
            case SubArticleNodeType.CDPC_Underwriting_Protect:
                subArticleID = updateSubArticle.PTI_SubArticle_ID;
                break;
            case SubArticleNodeType.CDAC_Truck_Terminal:
                subArticleID = updateSubArticle.TTM_SubArticle_ID;
                break;
            case SubArticleNodeType.CDUX_Primary:
                subArticleID = updateSubArticle.PRI_SubArticle_ID;
                break;
            case SubArticleNodeType.CDUX_Vehicle:
                subArticleID = updateSubArticle.VEH_SubArticle_ID;
                break;
            case SubArticleNodeType.CDUX_Watercraft:
                subArticleID = updateSubArticle.WAT_SubArticle_ID;
                break;
            case SubArticleNodeType.CDIM_Communications_TowerAntenna:
                subArticleID = updateSubArticle.CTA_SubArticle_ID;
                break;
            case SubArticleNodeType.CDIM_Schedule_Items:
                subArticleID = updateSubArticle.CIM_SubArticle_ID;
                break;
            case SubArticleNodeType.CDIM_Transportation_Detail:
                subArticleID = updateSubArticle.TRD_SubArticle_ID;
                break;
            case SubArticleNodeType.CDIM_Builders_Risk_Rigging_Item:
                subArticleID = updateSubArticle.BRI_SubArticle_ID;
                break;
            case SubArticleNodeType.CDCR_Employees:
                subArticleID = updateSubArticle.CRE_SubArticle_ID;
                break;
            case SubArticleNodeType.CDCR_Money_Securities:
                subArticleID = updateSubArticle.CRS_SubArticle_ID;
                break;
            case SubArticleNodeType.CDCR_Safe_Vault_Info:
                subArticleID = updateSubArticle.CRV_SubArticle_ID;
                break;
            case SubArticleNodeType.CDJB_Rating_Exhibition:
                subArticleID = updateSubArticle.JBE_SubArticle_ID;
                break;
            case SubArticleNodeType.CDJB_Rating_Off_Premises:
                subArticleID = updateSubArticle.JBO_SubArticle_ID;
                break;
            case SubArticleNodeType.CDJB_Underwriting_Protect:
                subArticleID = updateSubArticle.JBP_SubArticle_ID;
                break;
            case SubArticleNodeType.CDPP_Item:
                subArticleID = updateSubArticle.PPI_SubArticle_ID;
                break;
            default:
                console.error(
                    'Unsupported SubArticle NodeType provided for cloneSubArticle()  Parameter=' + updateSubArticle.NodeType
                );
                return;
        }

        subArticleID += '-' + updateSubArticle.nodeType; //State pointer for SubArticle is a combo of subArticleID + '-' + nodeType.

        this.helper.setItemChanged(updateSubArticle, state.subArticle[subArticleID].Edit_Status);

        sc.patchState({
            subArticle: {
                ...state.subArticle,
                [subArticleID]: {
                    ...state.subArticle[subArticleID],
                    ...updateSubArticle
                }
            }
        });
        sc.dispatch(new UpdateArticle({ Article_ID: articleID }));
    }

    @Action(AddSubArticle)
    addSubArticle(sc: StateContext<ArticleStateModel>, action: AddSubArticle) {
        const state = sc.getState();
        let newID = this.helper.getTempID();
        let newPointerID = newID + '-' + action.subArticleType;
        let newSubArticle = this.helper.createDefaultItem(ContractObjectDefaults, action.subArticleType, action.subArticle);

        let articleID = action.articleID;
        let updateArticle = cloneDeep(state.article[articleID]);

        this.helper.setItemAdded(newSubArticle);
        newSubArticle.Article_ID = articleID;
        newSubArticle.nodeType = action.subArticleType;

        // set values based on sub_article type
        switch (newSubArticle.nodeType) {
            case SubArticleNodeType.CDGL_Class_Product:
                newSubArticle.GLP_SubArticle_ID = newID;
                updateArticle.CDGL_Class_Product = this.helper.addArrayItem(updateArticle.CDGL_Class_Product, newPointerID);
                break;
            case SubArticleNodeType.CDAA_Driver_Vehicle:
                newSubArticle.DVH_SubArticle_ID = newID;
                updateArticle.CDAA_Driver_Vehicle = this.helper.addArrayItem(updateArticle.CDAA_Driver_Vehicle, newPointerID);
                break;
            case SubArticleNodeType.CDAA_Driver_Violation:
                newSubArticle.DVI_SubArticle_ID = newID;
                updateArticle.CDAA_Driver_Violation = this.helper.addArrayItem(
                    updateArticle.CDAA_Driver_Violation,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDMM_Individual_Schedule:
                newSubArticle.Indiv_SubArticle_ID = newID;
                updateArticle.CDMM_Individual_Schedule = this.helper.addArrayItem(
                    updateArticle.CDMM_Individual_Schedule,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDAA_Driver_Discount:
                newSubArticle.DDS_SubArticle_ID = newID;
                updateArticle.CDAA_Driver_Discount = this.helper.addArrayItem(updateArticle.CDAA_Driver_Discount, newPointerID);
                break;
            case SubArticleNodeType.CDAA_Driver_FinResp:
                newSubArticle.DFR_SubArticle_ID = newID;
                updateArticle.CDAA_Driver_FinResp = this.helper.addArrayItem(updateArticle.CDAA_Driver_FinResp, newPointerID);
                break;
            case SubArticleNodeType.CDAC_Filing_Detail:
                let contract = this.store.selectSnapshot(ContractState.contract);
                newSubArticle.FIL_SubArticle_ID = newID;
                newSubArticle.Filing_Exp_Date = contract.Exp_Date;
                updateArticle.CDAC_Filing_Detail = this.helper.addArrayItem(updateArticle.CDAC_Filing_Detail, newPointerID);
                break;
            case SubArticleNodeType.CDPC_Underwriting_Protect:
                newSubArticle.PTI_SubArticle_ID = newID;
                updateArticle.CDPC_Underwriting_Protect = this.helper.addArrayItem(
                    updateArticle.CDPC_Underwriting_Protect,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDAC_Truck_Terminal:
                newSubArticle.TTM_SubArticle_ID = newID;
                updateArticle.CDAC_Truck_Terminal = this.helper.addArrayItem(updateArticle.CDAC_Truck_Terminal, newPointerID);
                break;
            case SubArticleNodeType.CDUX_Primary:
                newSubArticle.PRI_SubArticle_ID = newID;
                updateArticle.CDUX_Primary = this.helper.addArrayItem(updateArticle.CDUX_Primary, newPointerID);
                break;
            case SubArticleNodeType.CDUX_Vehicle:
                newSubArticle.VEH_SubArticle_ID = newID;
                updateArticle.CDUX_Vehicle = this.helper.addArrayItem(updateArticle.CDUX_Vehicle, newPointerID);
                break;
            case SubArticleNodeType.CDUX_Watercraft:
                newSubArticle.WAT_SubArticle_ID = newID;
                updateArticle.CDUX_Watercraft = this.helper.addArrayItem(updateArticle.CDUX_Watercraft, newPointerID);
                break;
            case SubArticleNodeType.CDIM_Communications_TowerAntenna:
                newSubArticle.CTA_SubArticle_ID = newID;
                updateArticle.CDIM_Communications_TowerAntenna = this.helper.addArrayItem(
                    updateArticle.CDIM_Communications_TowerAntenna,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDIM_Schedule_Items:
                newSubArticle.CIM_SubArticle_ID = newID;
                updateArticle.CDIM_Schedule_Items = this.helper.addArrayItem(updateArticle.CDIM_Schedule_Items, newPointerID);
                break;
            case SubArticleNodeType.CDIM_Transportation_Detail:
                newSubArticle.TRD_SubArticle_ID = newID;
                updateArticle.CDIM_Transportation_Detail = this.helper.addArrayItem(
                    updateArticle.CDIM_Transportation_Detail,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDIM_Builders_Risk_Rigging_Item:
                newSubArticle.BRI_SubArticle_ID = newID;
                updateArticle.CDIM_Builders_Risk_Rigging_Item = this.helper.addArrayItem(
                    updateArticle.CDIM_Builders_Risk_Rigging_Item,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDCR_Employees:
                newSubArticle.CRE_SubArticle_ID = newID;
                updateArticle.CDCR_Employees = this.helper.addArrayItem(updateArticle.CDCR_Employees, newPointerID);
                break;
            case SubArticleNodeType.CDCR_Money_Securities:
                newSubArticle.CRS_SubArticle_ID = newID;
                updateArticle.CDCR_Money_Securities = this.helper.addArrayItem(
                    updateArticle.CDCR_Money_Securities,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDCR_Safe_Vault_Info:
                newSubArticle.CRV_SubArticle_ID = newID;
                updateArticle.CDCR_Safe_Vault_Info = this.helper.addArrayItem(updateArticle.CDCR_Safe_Vault_Info, newPointerID);
                break;
            case SubArticleNodeType.CDJB_Rating_Exhibition:
                newSubArticle.JBE_SubArticle_ID = newID;
                updateArticle.CDJB_Rating_Exhibition = this.helper.addArrayItem(
                    updateArticle.CDJB_Rating_Exhibition,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDJB_Rating_Off_Premises:
                newSubArticle.JBO_SubArticle_ID = newID;
                updateArticle.CDJB_Rating_Off_Premises = this.helper.addArrayItem(
                    updateArticle.CDJB_Rating_Off_Premises,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDJB_Underwriting_Protect:
                newSubArticle.JBP_SubArticle_ID = newID;
                updateArticle.CDJB_Underwriting_Protect = this.helper.addArrayItem(
                    updateArticle.CDJB_Underwriting_Protect,
                    newPointerID
                );
                break;
            case SubArticleNodeType.CDPP_Item:
                newSubArticle.PPI_SubArticle_ID = newID;
                updateArticle.CDPP_Item = this.helper.addArrayItem(updateArticle.CDPP_Item, newPointerID);
                break;
            default:
                console.error(
                    'Unsupported SubArticle NodeType provided for addSubArticle()  Parameter=' + newSubArticle.NodeType
                );
                return;
        }

        sc.patchState({
            subArticle: { ...state.subArticle, [newPointerID]: newSubArticle }
        });

        sc.dispatch(new UpdateArticle(updateArticle));
    }

    @Action(DeleteSubArticle)
    deleteSubArticle(sc: StateContext<ArticleStateModel>, action: DeleteSubArticle) {
        const state = sc.getState();
        let subArticleID = action.subArticleID;
        let subArticleNodeType: SubArticleNodeType = state.subArticle[subArticleID].nodeType;
        let subArticle = { ...state.subArticle }; //shallow copy subarticle state

        let articleID = action.articleID;
        let updateArticle = cloneDeep(state.article[articleID]);

        if (subArticleID.startsWith('-')) {
            // set values based on sub_article type
            switch (subArticleNodeType) {
                case SubArticleNodeType.CDGL_Class_Product:
                    updateArticle.CDGL_Class_Product = this.helper.removeArrayItemByValue(
                        updateArticle.CDGL_Class_Product,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDAA_Driver_Vehicle:
                    if (updateArticle) {
                        //If Sub Article added from the Vehicle, it may not reference the Driver Article Yet.
                        updateArticle.CDAA_Driver_Vehicle = this.helper.removeArrayItemByValue(
                            updateArticle.CDAA_Driver_Vehicle,
                            subArticleID
                        );
                    }
                    break;
                case SubArticleNodeType.CDAA_Driver_Violation:
                    updateArticle.CDAA_Driver_Violation = this.helper.removeArrayItemByValue(
                        updateArticle.CDAA_Driver_Violation,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDMM_Individual_Schedule:
                    updateArticle.CDMM_Individual_Schedule = this.helper.removeArrayItemByValue(
                        updateArticle.CDMM_Individual_Schedule,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDAA_Driver_Discount:
                    updateArticle.CDAA_Driver_Discount = this.helper.removeArrayItemByValue(
                        updateArticle.CDAA_Driver_Discount,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDAA_Driver_FinResp:
                    updateArticle.CDAA_Driver_FinResp = this.helper.removeArrayItemByValue(
                        updateArticle.CDAA_Driver_FinResp,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDAC_Filing_Detail:
                    updateArticle.CDAC_Filing_Detail = this.helper.removeArrayItemByValue(
                        updateArticle.CDAC_Filing_Detail,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDPC_Underwriting_Protect:
                    updateArticle.CDPC_Underwriting_Protect = this.helper.removeArrayItemByValue(
                        updateArticle.CDPC_Underwriting_Protect,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDAC_Truck_Terminal:
                    updateArticle.CDAC_Truck_Terminal = this.helper.removeArrayItemByValue(
                        updateArticle.CDAC_Truck_Terminal,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDUX_Primary:
                    updateArticle.CDUX_Primary = this.helper.removeArrayItemByValue(updateArticle.CDUX_Primary, subArticleID);
                    break;
                case SubArticleNodeType.CDUX_Vehicle:
                    updateArticle.CDUX_Vehicle = this.helper.removeArrayItemByValue(updateArticle.CDUX_Vehicle, subArticleID);
                    break;
                case SubArticleNodeType.CDUX_Watercraft:
                    updateArticle.CDUX_Watercraft = this.helper.removeArrayItemByValue(
                        updateArticle.CDUX_Watercraft,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDIM_Communications_TowerAntenna:
                    updateArticle.CDIM_Communications_TowerAntenna = this.helper.removeArrayItemByValue(
                        updateArticle.CDIM_Communications_TowerAntenna,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDIM_Schedule_Items:
                    updateArticle.CDIM_Schedule_Items = this.helper.removeArrayItemByValue(
                        updateArticle.CDIM_Schedule_Items,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDIM_Transportation_Detail:
                    updateArticle.CDIM_Transportation_Detail = this.helper.removeArrayItemByValue(
                        updateArticle.CDIM_Transportation_Detail,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDIM_Builders_Risk_Rigging_Item:
                    updateArticle.CDIM_Builders_Risk_Rigging_Item = this.helper.removeArrayItemByValue(
                        updateArticle.CDIM_Builders_Risk_Rigging_Item,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDCR_Employees:
                    updateArticle.CDCR_Employees = this.helper.removeArrayItemByValue(updateArticle.CDCR_Employees, subArticleID);
                    break;
                case SubArticleNodeType.CDCR_Money_Securities:
                    updateArticle.CDCR_Money_Securities = this.helper.removeArrayItemByValue(
                        updateArticle.CDCR_Money_Securities,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDCR_Safe_Vault_Info:
                    updateArticle.CDCR_Safe_Vault_Info = this.helper.removeArrayItemByValue(
                        updateArticle.CDCR_Safe_Vault_Info,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDJB_Rating_Exhibition:
                    updateArticle.CDJB_Rating_Exhibition = this.helper.removeArrayItemByValue(
                        updateArticle.CDJB_Rating_Exhibition,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDJB_Rating_Off_Premises:
                    updateArticle.CDJB_Rating_Off_Premises = this.helper.removeArrayItemByValue(
                        updateArticle.CDJB_Rating_Off_Premises,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDJB_Underwriting_Protect:
                    updateArticle.CDJB_Underwriting_Protect = this.helper.removeArrayItemByValue(
                        updateArticle.CDJB_Underwriting_Protect,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDPP_Item:
                    updateArticle.CDPP_Item = this.helper.removeArrayItemByValue(updateArticle.CDPP_Item, subArticleID);
                    break;
                default:
                    console.error(
                        'Unsupported SubArticle NodeType provided for deleteSubArticle()  Parameter=' + subArticleNodeType
                    );
                    return;
            }
        }
        this.helper.setItemDeleted(subArticle, subArticleID);

        sc.patchState({ subArticle: subArticle });

        if (updateArticle) {
            sc.dispatch(new UpdateArticle(updateArticle));
        }
    }

    @Action(RestoreSubArticle)
    restoreSubArticle(sc: StateContext<ArticleStateModel>, action: RestoreSubArticle) {
        const state = sc.getState();
        let subArticle = { ...state.subArticle }; //shallow copy subarticle state
        this.helper.setItemRestored(subArticle, action.subArticleID);
        sc.patchState({ subArticle: subArticle });
    }

    @Action(AddVehicleDriverSubArticle)
    addVehicleDriverSubArticle(sc: StateContext<ArticleStateModel>, action: AddVehicleDriverSubArticle) {
        const state = sc.getState();
        let newID = this.helper.getTempID();
        let newPointerID = newID + '-' + SubArticleNodeType.CDAA_Driver_Vehicle;
        let newSubArticle = this.helper.createDefaultItem(
            ContractObjectDefaults,
            SubArticleNodeType.CDAA_Driver_Vehicle,
            action.subArticle
        );

        this.helper.setItemAdded(newSubArticle);
        newSubArticle.nodeType = SubArticleNodeType.CDAA_Driver_Vehicle;
        newSubArticle.Book_Veh_Num = action.vehNum;
        newSubArticle.DVH_SubArticle_ID = newID;

        sc.patchState({
            subArticle: { ...state.subArticle, [newPointerID]: newSubArticle }
        });
    }

    @Action(UpdateVehicleDriverSubArticle)
    updateVehicleDriverSubArticle(sc: StateContext<ArticleStateModel>, action: UpdateVehicleDriverSubArticle) {
        const state = sc.getState();

        let updateSubArticle: any = { ...action.subArticle }; //need DEEP copy?
        let subArticleID = updateSubArticle.DVH_SubArticle_ID + '-' + SubArticleNodeType.CDAA_Driver_Vehicle;
        let currentSubArticle: any = { ...state.subArticle[subArticleID] };

        let updateArticle = cloneDeep(state.article[updateSubArticle.Article_ID]);

        this.helper.setItemChanged(updateSubArticle, state.subArticle[subArticleID].Edit_Status);

        // Check if Excluded_Driver_Ind was checked, then clear Use_Pct
        if (
            updateSubArticle.Excluded_Driver_Ind !== currentSubArticle.Excluded_Driver_Ind &&
            updateSubArticle.Excluded_Driver_Ind == 'Y'
        ) {
            updateSubArticle.Use_Pct = '';
        }

        // Check if Use_Pct was changed, then validate sum of Use_Pct !> 100
        if (updateSubArticle.Use_Pct !== currentSubArticle.Use_Pct) {
            let useSum = 0;
            let subArticleState = cloneDeep(state.subArticle);
            for (var i in subArticleState) {
                if (
                    subArticleState[i].nodeType == SubArticleNodeType.CDAA_Driver_Vehicle &&
                    subArticleState[i].Book_Veh_Num == updateSubArticle.Book_Veh_Num
                ) {
                    if (subArticleState[i].DVH_SubArticle_ID == updateSubArticle.DVH_SubArticle_ID) {
                        useSum += parseInt(updateSubArticle.Use_Pct, 10);
                    } else {
                        useSum += parseInt(subArticleState[i].Use_Pct, 10);
                    }
                }
            }
            if (useSum > 100) {
                updateSubArticle.Use_Pct = '';
            }
        }

        sc.patchState({
            subArticle: {
                ...state.subArticle,
                [subArticleID]: {
                    ...state.subArticle[subArticleID],
                    ...updateSubArticle
                }
            }
        });

        if (updateSubArticle.Article_ID != currentSubArticle.Article_ID) {
            //Add Pointer
            updateArticle.CDAA_Driver_Vehicle = this.helper.addArrayItem(updateArticle.CDAA_Driver_Vehicle, subArticleID);
        }

        // Check if Pricipal_Driver_Ind was checked, then uncheck all others
        if (
            updateSubArticle.Principal_Driver_Ind !== currentSubArticle.Principal_Driver_Ind &&
            updateSubArticle.Principal_Driver_Ind == 'Y'
        ) {
            let subArticleState = cloneDeep(state.subArticle);
            for (var i in subArticleState) {
                if (
                    subArticleState[i].nodeType == SubArticleNodeType.CDAA_Driver_Vehicle &&
                    subArticleState[i].Book_Veh_Num == updateSubArticle.Book_Veh_Num
                ) {
                    this.helper.setItemChanged(subArticleState[i], subArticleState[i].Edit_Status);

                    if (subArticleState[i].DVH_SubArticle_ID == updateSubArticle.DVH_SubArticle_ID) {
                        subArticleState[i].Principal_Driver_Ind = 'Y';
                    } else {
                        subArticleState[i].Principal_Driver_Ind = 'N';
                    }

                    // Change Edit_Status and Update parent Driver Article
                    let updateDriverArticle = cloneDeep(state.article[subArticleState[i].Article_ID]);
                    this.helper.setItemChanged(updateDriverArticle, updateDriverArticle.Edit_Status);
                    if (updateDriverArticle.Article_ID != updateArticle.Article_ID) {
                        sc.dispatch(new UpdateArticle(updateDriverArticle));
                    }
                }
            }

            sc.patchState({ subArticle: subArticleState });
        }

        // Check if Governing_Driver_Ind was checked, then uncheck all others
        if (
            updateSubArticle.Governing_Driver_Ind !== currentSubArticle.Governing_Driver_Ind &&
            updateSubArticle.Governing_Driver_Ind == 'Y'
        ) {
            let subArticleState = cloneDeep(state.subArticle);
            for (var i in subArticleState) {
                if (
                    subArticleState[i].nodeType == SubArticleNodeType.CDAA_Driver_Vehicle &&
                    subArticleState[i].Book_Veh_Num == updateSubArticle.Book_Veh_Num
                ) {
                    this.helper.setItemChanged(subArticleState[i], subArticleState[i].Edit_Status);

                    if (subArticleState[i].DVH_SubArticle_ID == updateSubArticle.DVH_SubArticle_ID) {
                        subArticleState[i].Governing_Driver_Ind = 'Y';
                    } else {
                        subArticleState[i].Governing_Driver_Ind = 'N';
                    }

                    // Change Edit_Status and Update parent Driver Article
                    let updateDriverArticle = cloneDeep(state.article[subArticleState[i].Article_ID]);
                    this.helper.setItemChanged(updateDriverArticle, updateDriverArticle.Edit_Status);
                    if (updateDriverArticle.Article_ID != updateArticle.Article_ID) {
                        sc.dispatch(new UpdateArticle(updateDriverArticle));
                    }
                }
            }

            sc.patchState({ subArticle: subArticleState });
        }

        sc.dispatch(new UpdateArticle(updateArticle));
    }

    @Action(AddArticleInterestPointer)
    addArticleInterestPointer(sc: StateContext<ArticleStateModel>, action: AddArticleInterestPointer) {
        const state = sc.getState();
        let articleID = action.articleID;
        let updateArticle = {
            ...state.article[articleID]
        };

        if (!updateArticle.Interest) {
            updateArticle.Interest = [];
        }

        updateArticle.Interest = [...updateArticle.Interest, action.interestID];

        sc.patchState({
            article: {
                ...state.article,
                [articleID]: {
                    ...state.article[articleID],
                    ...updateArticle
                }
            }
        });
    }

    @Action(RemoveArticleInterestPointer)
    removeArticleInterestPointer(sc: StateContext<ArticleStateModel>, action: RemoveArticleInterestPointer) {
        const state = sc.getState();

        let articleID = action.articleID;
        let updateArticle = {
            ...state.article[articleID]
        };

        updateArticle.Interest = this.helper.removeArrayItemByValue(updateArticle.Interest, action.interestID);

        sc.patchState({
            article: {
                ...state.article,
                [articleID]: {
                    ...state.article[articleID],
                    ...updateArticle
                }
            }
        });
    }

    @Action(SetPrimaryPerilProduct)
    SetPrimaryPerilProduct(sc: StateContext<ArticleStateModel>, action: SetPrimaryPerilProduct) {
        const state = sc.getState();
        let articleID = action.articleID;
        let updateArticle: any = cloneDeep(state.article[articleID]);

        // Only sets the value of Article.Primary_Peril.
        // No Edit_Status changes on article or parent line record
        updateArticle.Primary_Peril = action.productCode;

        sc.patchState({
            article: {
                ...state.article,
                [articleID]: {
                    ...state.article[articleID],
                    ...updateArticle
                }
            }
        });
    }

    @Action(DeleteSubArticleOnParentDeleted)
    deleteSubArticleOnParentDeleted(sc: StateContext<ArticleStateModel>, action: DeleteSubArticleOnParentDeleted) {
        const state = sc.getState();
        let subArticleID = action.subArticleID;
        let subArticleNodeType: SubArticleNodeType = state.subArticle[subArticleID].nodeType;
        let subArticle = { ...state.subArticle }; //shallow copy subarticle state

        let articleID = action.articleID;
        let updateArticle = cloneDeep(state.article[articleID]);

        if (subArticleID.startsWith('-')) {
            // set values based on sub_article type
            switch (subArticleNodeType) {
                case SubArticleNodeType.CDGL_Class_Product:
                    updateArticle.CDGL_Class_Product = this.helper.removeArrayItemByValue(
                        updateArticle.CDGL_Class_Product,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDAA_Driver_Vehicle:
                    if (updateArticle) {
                        //If Sub Article added from the Vehicle, it may not reference the Driver Article Yet.
                        updateArticle.CDAA_Driver_Vehicle = this.helper.removeArrayItemByValue(
                            updateArticle.CDAA_Driver_Vehicle,
                            subArticleID
                        );
                    }
                    break;
                case SubArticleNodeType.CDAA_Driver_Violation:
                    updateArticle.CDAA_Driver_Violation = this.helper.removeArrayItemByValue(
                        updateArticle.CDAA_Driver_Violation,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDMM_Individual_Schedule:
                    updateArticle.CDMM_Individual_Schedule = this.helper.removeArrayItemByValue(
                        updateArticle.CDMM_Individual_Schedule,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDAA_Driver_Discount:
                    updateArticle.CDAA_Driver_Discount = this.helper.removeArrayItemByValue(
                        updateArticle.CDAA_Driver_Discount,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDAA_Driver_FinResp:
                    updateArticle.CDAA_Driver_FinResp = this.helper.removeArrayItemByValue(
                        updateArticle.CDAA_Driver_FinResp,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDAC_Filing_Detail:
                    updateArticle.CDAC_Filing_Detail = this.helper.removeArrayItemByValue(
                        updateArticle.CDAC_Filing_Detail,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDPC_Underwriting_Protect:
                    updateArticle.CDPC_Underwriting_Protect = this.helper.removeArrayItemByValue(
                        updateArticle.CDPC_Underwriting_Protect,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDAC_Truck_Terminal:
                    updateArticle.CDAC_Truck_Terminal = this.helper.removeArrayItemByValue(
                        updateArticle.CDAC_Truck_Terminal,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDUX_Primary:
                    updateArticle.CDUX_Primary = this.helper.removeArrayItemByValue(updateArticle.CDUX_Primary, subArticleID);
                    break;
                case SubArticleNodeType.CDUX_Vehicle:
                    updateArticle.CDUX_Vehicle = this.helper.removeArrayItemByValue(updateArticle.CDUX_Vehicle, subArticleID);
                    break;
                case SubArticleNodeType.CDUX_Watercraft:
                    updateArticle.CDUX_Watercraft = this.helper.removeArrayItemByValue(
                        updateArticle.CDUX_Watercraft,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDIM_Communications_TowerAntenna:
                    updateArticle.CDIM_Communications_TowerAntenna = this.helper.removeArrayItemByValue(
                        updateArticle.CDIM_Communications_TowerAntenna,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDIM_Schedule_Items:
                    updateArticle.CDIM_Schedule_Items = this.helper.removeArrayItemByValue(
                        updateArticle.CDIM_Schedule_Items,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDIM_Transportation_Detail:
                    updateArticle.CDIM_Transportation_Detail = this.helper.removeArrayItemByValue(
                        updateArticle.CDIM_Transportation_Detail,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDIM_Builders_Risk_Rigging_Item:
                    updateArticle.CDIM_Builders_Risk_Rigging_Item = this.helper.removeArrayItemByValue(
                        updateArticle.CDIM_Builders_Risk_Rigging_Item,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDCR_Employees:
                    updateArticle.CDCR_Employees = this.helper.removeArrayItemByValue(updateArticle.CDCR_Employees, subArticleID);
                    break;
                case SubArticleNodeType.CDCR_Money_Securities:
                    updateArticle.CDCR_Money_Securities = this.helper.removeArrayItemByValue(
                        updateArticle.CDCR_Money_Securities,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDCR_Safe_Vault_Info:
                    updateArticle.CDCR_Safe_Vault_Info = this.helper.removeArrayItemByValue(
                        updateArticle.CDCR_Safe_Vault_Info,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDJB_Rating_Exhibition:
                    updateArticle.CDJB_Rating_Exhibition = this.helper.removeArrayItemByValue(
                        updateArticle.CDJB_Rating_Exhibition,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDJB_Rating_Off_Premises:
                    updateArticle.CDJB_Rating_Off_Premises = this.helper.removeArrayItemByValue(
                        updateArticle.CDJB_Rating_Off_Premises,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDJB_Underwriting_Protect:
                    updateArticle.CDJB_Underwriting_Protect = this.helper.removeArrayItemByValue(
                        updateArticle.CDJB_Underwriting_Protect,
                        subArticleID
                    );
                    break;
                case SubArticleNodeType.CDPP_Item:
                    updateArticle.CDPP_Item = this.helper.removeArrayItemByValue(updateArticle.CDPP_Item, subArticleID);
                    break;
                default:
                    console.error(
                        'Unsupported SubArticle NodeType provided for deleteSubArticle()  Parameter=' + subArticleNodeType
                    );
                    return;
            }
        }
        this.helper.setItemDeleted(subArticle, subArticleID);

        sc.patchState({ subArticle: subArticle });
    }
}
